import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CheckboxLine, FilterSelect, HpButton, HpTag, Icon, PageHeading, TableScroll } from '@hp/ui';
import { useCan } from '../api/me';
import { trpc } from '../api/trpc';
import { StatusBadge } from '../components/badges';
import { ErrorAlert } from '../components/error-alert';
import { RelativeTime } from '../components/relative-time';
import { BrutTable, BrutTheadRow } from '../components/ui/table';
import { invalidateSchedules, useSchedules, type Schedule } from '../features/schedules/queries';

/** Interval choices offered in the select; anything else shows as a read-only "custom" entry. */
const PRESET_MINUTES = [15, 30, 60, 120, 240, 360, 720] as const;
const DAILY = 'daily';
const CUSTOM = 'custom';
const DEFAULT_DAILY_TIME = '03:00';

/** "sync_active:7" → "sync_active" (the id after the colon is context, not part of the code). */
function reasonCode(reason: string): string {
  return reason.split(':')[0] ?? reason;
}

function OutcomeCell({ schedule }: { schedule: Schedule }) {
  const { t } = useTranslation(['schedules', 'common']);
  const outcome = schedule.lastOutcome;
  if (!outcome) return <span className="bo-muted">{t('outcome.never')}</span>;
  const failed = !outcome.fired && (outcome.reason ?? '').startsWith('error:');
  const status = outcome.fired ? 'completed' : failed ? 'failed' : 'skipped';
  const reason = outcome.reason ?? '';
  const text = outcome.fired
    ? t('outcome.fired', { runId: outcome.runId ?? 0 })
    : failed
      ? reason.replace(/^error:\s*/, '')
      : t(`reason.${reasonCode(reason)}`, { defaultValue: reason });
  return (
    <span className="bo-row">
      <StatusBadge status={status} />
      <span className="bo-clip" title={reason || undefined}>
        {text}
      </span>
      <span className="bo-muted">
        · {outcome.trigger === 'user' ? t('outcome.byUser') : t('outcome.bySchedule')}{' '}
        <RelativeTime value={outcome.at} />
      </span>
    </span>
  );
}

export default function Schedules() {
  const { t } = useTranslation(['schedules', 'common']);
  const canRun = useCan()('crm.run_jobs');
  const schedules = useSchedules();
  const update = useMutation(trpc.scheduler.update.mutationOptions({ onSuccess: invalidateSchedules }));
  const runNow = useMutation(trpc.scheduler.runNow.mutationOptions({ onSuccess: invalidateSchedules }));
  const busy = update.isPending || runNow.isPending;
  const rows = schedules.data ?? [];
  const schedulerOff = rows.length > 0 && rows.every((s) => !s.schedulerEnabled);

  function intervalValue(s: Schedule): string {
    if (s.atTime !== null) return DAILY;
    return PRESET_MINUTES.some((m) => m === s.everyMinutes) ? String(s.everyMinutes) : CUSTOM;
  }

  function changeInterval(s: Schedule, value: string) {
    if (value === CUSTOM) return;
    if (value === DAILY) update.mutate({ key: s.key, everyMinutes: 1440, atTime: s.atTime ?? DEFAULT_DAILY_TIME });
    else update.mutate({ key: s.key, everyMinutes: Number(value), atTime: null });
  }

  return (
    <div className="bo-sections">
      <PageHeading title={t('title')} description={t('description')} />

      <ErrorAlert error={schedules.error ?? update.error ?? runNow.error} />
      {schedulerOff && (
        <p className="bo-hint bo-hint--danger">
          <Icon name="alert" />
          {t('schedulerOff')}
        </p>
      )}

      <section className="hp-card">
        <TableScroll label={t('tableLabel')}>
          <BrutTable>
            <BrutTheadRow>
              <th>{t('columns.schedule')}</th>
              <th>{t('columns.interval')}</th>
              <th>{t('columns.enabled')}</th>
              <th>{t('columns.nextRun')}</th>
              <th>{t('columns.lastFired')}</th>
              <th>{t('columns.lastOutcome')}</th>
              <th />
            </BrutTheadRow>
            <tbody>
              {rows.map((s) => {
                const value = intervalValue(s);
                const editable = canRun && s.known && !busy;
                return (
                  <tr key={s.key} className={s.enabled ? undefined : 'bo-row--inactive'}>
                    <td>
                      <span className="bo-strong">{t(`name.${s.key}`, { defaultValue: s.description })}</span>
                      <br />
                      <small className="bo-mono bo-muted">{s.key}</small>
                      {!s.known && (
                        <>
                          {' '}
                          <HpTag tone="warning">{t('unknownSpec')}</HpTag>
                        </>
                      )}
                    </td>
                    <td>
                      <span className="bo-row">
                        <FilterSelect
                          value={value}
                          disabled={!editable}
                          aria-label={t('intervalAria', { key: s.key })}
                          onChange={(e) => changeInterval(s, e.target.value)}
                        >
                          {PRESET_MINUTES.map((m) => (
                            <option key={m} value={String(m)}>
                              {t(`interval.every${m}`)}
                            </option>
                          ))}
                          <option value={DAILY}>{t('interval.daily')}</option>
                          {value === CUSTOM && (
                            <option value={CUSTOM}>{t('interval.custom', { count: s.everyMinutes })}</option>
                          )}
                        </FilterSelect>
                        {s.atTime !== null && (
                          <input
                            type="time"
                            value={s.atTime}
                            disabled={!editable}
                            aria-label={t('dailyTimeAria', { key: s.key })}
                            onChange={(e) => {
                              if (e.target.value) update.mutate({ key: s.key, atTime: e.target.value });
                            }}
                          />
                        )}
                      </span>
                    </td>
                    <td>
                      <CheckboxLine
                        checked={s.enabled}
                        disabled={!editable}
                        aria-label={t('enabledAria', { key: s.key })}
                        onChange={(e) => update.mutate({ key: s.key, enabled: e.target.checked })}
                      >
                        {s.enabled ? t('common:status.active') : t('common:status.inactive')}
                      </CheckboxLine>
                    </td>
                    <td>{s.enabled ? <RelativeTime value={s.nextRunAt} /> : <span className="bo-muted">—</span>}</td>
                    <td>
                      <RelativeTime value={s.lastFiredAt} />
                    </td>
                    <td>
                      <OutcomeCell schedule={s} />
                    </td>
                    <td className="bo-cell-actions">
                      {canRun && s.known && (
                        <HpButton
                          className="bo-button--sm"
                          variant="secondary"
                          icon="play"
                          busy={runNow.isPending && runNow.variables?.key === s.key}
                          disabled={busy}
                          onClick={() => runNow.mutate({ key: s.key })}
                        >
                          {t('runNow')}
                        </HpButton>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!schedules.isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="bo-cell-empty">
                    {t('empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </BrutTable>
        </TableScroll>
        <p className="hp-card__body bo-hint">{t('timezoneNote')}</p>
      </section>
    </div>
  );
}
